import {Injectable} from '@angular/core'
import {defaultSurveyConstructorParams, Survey} from 'app/models/Survey'
import {IHaveRules, ISurvey} from 'app/interfaces/ISurvey'
import {UserService} from 'app/core/user/user.service'
import {AngularFireDatabase} from '@angular/fire/compat/database'
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  Subscription,
  take
} from 'rxjs'
import {ToastrService} from 'ngx-toastr'
import { HttpClient } from '@angular/common/http'
import {environment} from 'environments/environment'
import {PublicationAccessService} from 'app/modules/landing-publication/publication-access.service'
import {UrlEntryService} from 'app/core/urlEntry/url-entry.service'
import {TranslocoService} from '@ngneat/transloco'
import {AuthService} from 'app/core/auth/auth.service'
import {Router} from '@angular/router'
import {UserError} from 'app/custom-errors/UserError'
import {Question} from 'app/models/questions/Question'
import {requireMinRowSelection} from 'app/form-validators/RequireMinRowSelectionValidator'
import {requireMinStatementSelection} from 'app/form-validators/RequireMinStatementSelectionValidator'
import {requireSelectionOnEachMatrixRow} from 'app/form-validators/RequireSelectionOnEachMatrixRowValidator'
import {RewardService} from "../../../reward/reward.service"
import {Matrix} from 'app/models/questions/Matrix'
import {EventMessagesService, eventType} from "@store/event-messages.service"
import {requireMinImageSelection} from 'app/form-validators/RequireMinImageSelectionValidator'
import Bugsnag from '@bugsnag/js'
import {AppModeService} from '@store/services/app-mode.service'
import {AppMode, AppPreviewMode} from 'app/enums/AppModeEnum'
import {EditResponseService} from "../../survey-response-edit/edit-response.service";
import {AbstractControl, AsyncValidatorFn, ValidationErrors} from "@angular/forms";
import {HoneyPotService} from "../survey-question/types/honey-pot/honey-pot.service";
import {cloneDeep} from "lodash-es";
import {TestSurveyService} from "../../../../../@fuse/components/test-survey/test-survey.service";
import {FlowFeedbackService} from "@store/flow-feedback/flow-feedback.service";
import {LangsListParamsService} from "@store/langs-list-params.service";
import {InputPersistenceService} from "./input-persistence.service";

@Injectable({
  providedIn: 'root'
})
export class SurveyViewerService {

  errorMessages = {
    minImageSelection: (required: number) => {
      const imageWrd = required > 1 ? 'images' : 'image'
      return {
        fr: `Sélectionnez au moins ${required} ${imageWrd}.`,
        en: `Choose at least ${required} ${imageWrd}.`
      }
    },
    minCheckboxSelection: (required: number) => {
      const choiceWrd = required > 1 ? 'choices' : 'choice'
      return {
        fr: `Sélectionnez au moins ${required} choix.`,
        en: `Select at least ${required} ${choiceWrd}.`
      }
    },
    minRowSelection: (required: number) => {
      const choiceWrd = required > 1 ? 'choices' : 'choice'
      return {
        fr: `Sélectionnez au moins ${required} choix par ligne.`,
        en: `Select at least ${required} ${choiceWrd} per line.`
      }
    },
    minStatementSelection: (required: number) => {
      const choiceWrd = required > 1 ? 'choices' : 'choice'
      return {
        fr: `Sélectionnez au moins ${required} choix par colonne.`,
        en: `Select at least ${required} ${choiceWrd} per column.`
      }
    }
  }

  alreadyElapsedTime: number;
  allFormControlsMarkedAsTouched$ = new BehaviorSubject<boolean>(false)
  isLoadingSurveyMeta$ = new BehaviorSubject<boolean>(false)
  hasPendingRequest$ = new BehaviorSubject<boolean>(false)
  activeSurvey$ = new BehaviorSubject<Survey>(null)
  haveRules$: BehaviorSubject<IHaveRules> = new BehaviorSubject<IHaveRules>(null);

  initialRawSurvey: ISurvey
  initialRawSurvey$ = new BehaviorSubject<ISurvey>(null)

  maxFillingRetries: number = 2000
  requireMinImageSelectionFnName = requireMinImageSelection.name
  minRowErrorFnName = requireMinRowSelection.name
  minStatementErrorFnName = requireMinStatementSelection.name
  requireSelectionForEachStatementFnName = requireSelectionOnEachMatrixRow.name
  defaultImageUrl = ''
  private _createUserOnSubmit: boolean;


  private formSub$: Subscription
  private answers$ = new BehaviorSubject<{}>(null)

  constructor(
    private db: AngularFireDatabase,
    private userService: UserService,
    private toastr: ToastrService,
    private http: HttpClient,
    private publicationAccessService: PublicationAccessService,
    private urlEntryService: UrlEntryService,
    private translocoService: TranslocoService,
    private authService: AuthService,
    private rewardService: RewardService,
    private router: Router,
    private eventMessagesService: EventMessagesService,
    private appModeService: AppModeService,
    private editResponseService: EditResponseService,
    private honeyPotService: HoneyPotService,
    private testSurveyService: TestSurveyService,
    private flowFeedbackService: FlowFeedbackService,
    private langsListParamsService: LangsListParamsService,
    private inputPersistence: InputPersistenceService,
  ) {

    combineLatest(
      [
        this.appModeService.isTestSurvey$,
        this.userService.user$,
        this.initialRawSurvey$
      ]
    )
      .pipe(
        distinctUntilChanged(),
        filter(([isTestSurvey, user, initialRawSurvey]) => isTestSurvey === true && !!user && !!initialRawSurvey)
      )
      .subscribe(([isTestSurvey, user, survey]) => {
        if (this.activeSurvey$.value) {
          if (this.appModeService.isTestSurvey$.value) {
            const toSave = {
              timeStamp: new Date().toISOString(),
              datas: this.buildDataPayload(),
            }
            this.testSurveyService.updateValuesForTest(this.haveRules$.value, toSave);
          }
          this.relaunchEvalRules();
        }
      });

  }

  changeFormUpdateStrategy(formControl: AbstractControl, strategy: 'blur' | 'change') {
    Object.defineProperty(formControl, 'updateOn', {
      get: () => strategy,
      configurable: true
    });
  }

  changeCreateUserOnSubmit(value: boolean) {
    this._createUserOnSubmit = value;
  }

  getRowLabelImageUrl(question: Matrix, rowKey: string, activeLang: string) {
    if (!question.rows[rowKey].media) {
      return this.defaultImageUrl
    }

    if (!question.rows[rowKey].media[activeLang]) {
      const availableLanguages = Object.keys(question.rows[rowKey].media)
      return question.rows[rowKey].media[availableLanguages.find(el => el)]['url']
    }

    return question.rows[rowKey].media[activeLang]['url']
  }

  getStatementLabelImageUrl(question: Question, statementKey: string, activeLang: string): string {
    if (!question.statements[statementKey].media) {
      return this.defaultImageUrl
    }

    if (!question.statements[statementKey].media[activeLang]) {
      const availableLanguages = Object.keys(question.statements[statementKey].media)
      return question.statements[statementKey].media[availableLanguages.find(el => el)]['url']
    }

    return question.statements[statementKey].media[activeLang]['url']
  }

  fillAnswers(): void {
    this.resetSurveyForm()
    try {
      this.activeSurvey$.value.getQuestionsReferencedInStructureElements().forEach(q => {
        q.isOptional ?
          this.fillRandomly(q) :
          q.fillFormWithDummyAnswer(this.activeSurvey$.value.form, 0, this.maxFillingRetries)
      })
      this.activeSurvey$.value.form.updateValueAndValidity()
    } catch (e) {
      Bugsnag.notify(e, (event) => {
        event.addMetadata('Survey Viewer Service', {
          surveyDef: this.activeSurvey$.value.surveyDef,
          surveyId: this.activeSurvey$.value.id
        })
      })
      console.error(e.message)
      console.error(e.stack)
      this.toastr.error(e, this.translocoService.translate('Erreur de remplissage du sondage'), {
        disableTimeOut: true
      })
    }
  }

  static allowRandomly() {
    return Math.random() <= 0.8
  }

  private fillRandomly(q: Question) {
    if (SurveyViewerService.allowRandomly()) {
      q.fillFormWithDummyAnswer(this.activeSurvey$.value.form, 0, this.maxFillingRetries)
    }
  }

  resetSurveyForm(): void {
    this.answers$.next(null)
    this.activeSurvey$.value.form.reset()
    this.initializeViewer(this.initialRawSurvey)
  }

  getSurvey(surveyId: number) {

    this.flowFeedbackService.surveyIsLoading(true);

    //
    // Mode Reply = Get Survey from Database
    //
    if (this.appModeService.appMode$.value === AppMode.REPLY || this.appModeService.appMode$.value === AppMode.EDIT_RESPONSE) {
      // if (this.appModeService.appMode$.value === AppMode.REPLY) {

      const endpoint = `${environment.apiUrl}/4dm1n/publicActions`
      this.isLoadingSurveyMeta$.next(true)

      const actionName = 'getSurvey'
      const body = {
        action: actionName,
        params: {
          surveyId,
          userId: this.userService.user.id
        }
      }

      this.http
        .post(endpoint, body)
        .subscribe({
          next: (response: {
            data: {
              datas: ISurvey
            }
          }) => {
            if (!response?.data?.datas || !Object.keys(response.data.datas).length) {
              this.toastr.error(this.translocoService.translate(`Impossible de récupérer le sondage à l'adresse`) + ` ${endpoint}`, this.translocoService.translate('Sondage non trouvé'), {
                disableTimeOut: true
              })
              this.isLoadingSurveyMeta$.next(false)
              return
            }

            if (response.data.datas.alreadyAnsweredByUser && this.appModeService.appMode$.value !== AppMode.EDIT_RESPONSE) {
              this.router.navigate(['/home']).then(() => {
                this.isLoadingSurveyMeta$.next(false)
              })
              this.flowFeedbackService.surveyIsLoading(false);
              return
            }

            const survey = response.data.datas
            this.initialRawSurvey = survey

            this.initializeViewer(survey)

            if (this.appModeService.appMode$.value === AppMode.EDIT_RESPONSE) {
              this.patchResponses(this.editResponseService.rawResultFromDatabase);
            } else {
              this.getAnswers(surveyId)
            }

            this.isLoadingSurveyMeta$.next(false)
            this.flowFeedbackService.surveyIsLoading(false);

          }, error: err => {
            this.isLoadingSurveyMeta$.next(false)
            Bugsnag.notify(err, (event) => {
              event.addMetadata('Survey Viewer Service', {
                method: this.getSurvey.name,
                body,
                endpoint
              })
            })
          }
        })

      return
    }

    //
    // Mode Preview = Get Survey from Firebase
    //

    const surveyUrl = `/surveys/${surveyId}`

    this.db.object(surveyUrl)
      .valueChanges()
      .subscribe({
        next: (response: ISurvey) => {

          if (!response) {
            this.toastr.error(this.translocoService.translate(`Impossible de récupérer le sondage à l'adresse`) + ` ${surveyUrl}`, this.translocoService.translate('Sondage non trouvé'), {
              disableTimeOut: true
            })
            Bugsnag.notify(new Error('No survey available at the specified address'), (event) => {
              event.addMetadata('Survey Viewer Service', {
                endpoint: surveyUrl,
                surveyId,
                appMode: this.appModeService.appMode$.value
              })
            })
            return
          }

          this.initialRawSurvey = response
          if (!this.initialRawSurvey.surveyDef.questions){
            this.initialRawSurvey.surveyDef.questions = this.initialRawSurvey.questions
          }

          this.flowFeedbackService.setInitializeViewer(true);
          this.initializeViewer(response)

          //
          // 2 Cas : si on est en mode preview on va aller chercher le Raw Result dans la BD
          //         sinon ce sera celui de Firebase
          //
          if (this.appModeService.appMode$.value === AppMode.EDIT_RESPONSE) {
            this.patchResponses(this.editResponseService.rawResultFromDatabase);
          } else {
            this.getAnswers(surveyId)
          }
          //
          // this.getAnswers(surveyId)
          //
          //
          //

          this.flowFeedbackService.setInitializeViewer(false);
          this.flowFeedbackService.surveyIsLoading(false);

        }, error: err => {
          Bugsnag.notify(err, (event) => {
            event.addMetadata('Survey Viewer Service', {
              method: this.getSurvey.name,
              endpoint: surveyUrl
            })
          })
        }
      })
  }

  userExistValidator(createUser: boolean): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => !createUser ? null :
      this.http.post(`${environment.apiUrl}/appCommunity/checkForEmail`,
        {action: 'checkForEmail', params: {email: control.value, surveyId: this.activeSurvey$.value.id}})
        .pipe(map((res) => res['data'] ? {userExist: {value: control.value}} : null));
  }

  submitSurvey() {
    if (this.hasPendingRequest$.value) {
      return false
    }

    if (!this.userService.user) {
      this.toastr.error(
        this.translocoService.translate('Aucun utilisateur en cours ?'),
        this.translocoService.translate(`Impossible d'enregistrer les réponses.`)
      )
      this.hasPendingRequest$.next(false)
      Bugsnag.notify(new UserError().isMissing())
      throw new UserError().isMissing()
    }

    if (this.appModeService.appMode$.value === AppMode.PREVIEW) {
      this.notifySuccessForPreviewMode()
      return false
    }

    //
    // Penser à sauvegarder une ultime fois dans firebasse
    //
    this.syncAnswersWithFirebase().then(() => {

      // Nettoyer toutes les valeurs temporaires sauvegardées lors de la soumission
      this.inputPersistence.clearAll();

      this.hasPendingRequest$.next(true)

      const endpoint = `${environment.apiUrl}/appCommunity/submitResponses`;
      const body = {
        ...this.buildSurveySubmitBodyPayload(),
        editResponse: this.appModeService.appMode$.value === AppMode.EDIT_RESPONSE,
        createUser: {
          status: this._createUserOnSubmit,
          commId: this.activeSurvey$.value.originalSurveyDef.specialInfo?.communityForNewUser
        },
      }

      this.http.post(endpoint, body).subscribe({

        next: (response: any) => {
          // Appel à la nouvelle lambda de validation AI
          this.callValidateAnswerWithAI(body, response);

          if (this.appModeService.appMode$.value === AppMode.EDIT_RESPONSE) {

            this.toastr.success(
              this.translocoService.translate('RESPONSE UPDATED'),
              '[EDIT RESPONSES]'
            )
            this.hasPendingRequest$.next(false)
            return false;

          } else {

            const userDetails = response.data.userDetails

            this.eventMessagesService.setSurveyId(parseInt(this.activeSurvey$.value.id))
            this.eventMessagesService.emitEvent(eventType.surveyReplied, {})

            this.toastr.success(
              this.translocoService.translate('Vos réponses ont bien été enregistrées.'),
              this.translocoService.translate('Réponses soumises.')
            )

            this.authService.accessToken = userDetails.user.jwtToken
            if (this.userService.user.publicStatus?.cintId) {
              window.location.replace(`https://s.cint.com/Survey/Complete?ProjectToken=${this.publicationAccessService.publication$.value.cintTokens?.complete}`)
            }
            this.updateUserExtProfiles(userDetails)

          }
        },

        error: err => {
          this.hasPendingRequest$.next(false)
          Bugsnag.notify(err, (event) => {
            event.addMetadata('Survey Viewer Service', {
              method: this.submitSurvey.name,
              payload: body,
              endpoint,
              errorMsg: err.error.message,
              jwtToken: this.authService.accessToken
            })
          })
          this.toastr.error(err.error.message, this.translocoService.translate(`Les réponses n'ont pu être enregistrées`), {
            disableTimeOut: true
          })
        }
      })


    });
  }

  private buildSurveySubmitBodyPayload() {
    return {
      surveyId: this.activeSurvey$.value.id,
      datas: this.buildDataPayload(true),
      isPublic: this.userService.user.isPublic,
      lang: this.translocoService.getActiveLang(),
      publicStatus: {
        ...this.userService.user.publicStatus,
        ext: this.userService.extractExtProfilesForPublicUser() || {},
      },
      finalSurveyDef: {},
      extProfileQuestions: this.extractExtProfileQuestions(),
      pubUuid: this.publicationAccessService?.pubUuid,

    }
  }

  private extractExtProfileQuestions() {
    let profileQuestions = []
    this.activeSurvey$.value.pages.forEach(p => {
      p.questions.forEach(q => {
        if (q.shouldSaveInExtProfile) {
          profileQuestions.push({
            keyName: q.key,
            keyValue: JSON.stringify(this.activeSurvey$.value.form.get(q.key).value)
          })
        }
      })
    })
    return profileQuestions
  }

  private updateUserExtProfiles(userDetails) {
    const endpoint = `${environment.apiUrl}/user/extprofils`
    return this.http.post(endpoint, {
      userId: userDetails.user.id,
      list: []
    }).subscribe({
      next: (response: any) => {
        let extProfileMap = {}
        response.data.exts.rows.forEach(r => extProfileMap[r.key_name] = r.key_value)
        this.userService.user$.next({...this.userService.user$.value, extProfiles: extProfileMap})
        this.relogUserAfterSubmitSurvey(userDetails.user.jwtToken)
        this.hasPendingRequest$.next(false)
      },
      error: err => {
        this.hasPendingRequest$.next(false)
        Bugsnag.notify(err, (event) => {
          event.addMetadata('Survey Viewer Service', {
            endpoint,
            user: userDetails,
            methodCalled: this.updateUserExtProfiles.name
          })
        })
      }
    })
  }

  private getAnswers(surveyId: number): void | false {
    if (this.appModeService.appMode$.value === AppMode.SINGLE_QUESTION) {
      return false
    }

    const answerUrl = `temp/${surveyId}/${this.userService.user.id}`
    this.db.object(answerUrl)
      .valueChanges()
      .subscribe((response: {
        datas?: any,
        timeStamp: string
      }) => {
        if (!response || !response.datas) {
          return
        }
        this.alreadyElapsedTime ??= response.datas?.elapsedDetail?.elapsedInSeconds || 0;
        this.patchResponses(response.datas);
      })
  }

  public patchResponses(datas) {

    this.answers$.next(datas)
    this.activeSurvey$.value.applyRules(datas)
    this.activeSurvey$.value.form.patchValue(datas, {emitEvent: false})
    this.activeSurvey$.next(this.activeSurvey$.value)
    this.applyMaxAnswers()

  }

  public relaunchEvalRules() {
    this.patchResponses(this.answers$.value);
  }

  private relogUserAfterSubmitSurvey(jwtToken: string) {
    this.authService.signInUsingToken(jwtToken)
      .pipe(take(1))
      .subscribe({
        next: () => {
          // Update la thunasse...
          this.rewardService.refreshRewardsUserInfo()
          this.publicationAccessService.evaluatePublicationRules(true)
          this.hasPendingRequest$.next(false)

          return this.publicationAccessService.pubUuid ?
            of(this.router.navigate(['/publication/display', this.publicationAccessService.pubUuid])) :
            of(this.router.navigate(['/home']))
        },
        error: err => {
          this.hasPendingRequest$.next(false)
          Bugsnag.notify(err, (event) => event.addMetadata('Survey Viewer Service', {jwtToken}))
        }
      })
  }

  private buildDataPayload(prepareForPOST = false) {

    //
    // On tronque si la publication a pas le flag 'saveFromFirebase'
    //
    const shrink = this.publicationAccessService.publication$.value?.generalInfo?.moreOptions?.saveFromFirebase || false;
    if (!shrink) {
      prepareForPOST = false;
    }

    const formattedAnswersOfActiveQuestions = {}
    if (!prepareForPOST) {
      this.activeSurvey$.value.pages.forEach(p => {
        p.questions.forEach(q => {
          if (q.keepInSurveyAnswers()) {
            formattedAnswersOfActiveQuestions[q.key] = q.formatFormPayload(this.activeSurvey$.value.form)
          }
        })
      })

    }

    return {
      ...formattedAnswersOfActiveQuestions,
      version: '2022.0',
      timeStamp: new Date().toISOString(),
      pubUuid: this.publicationAccessService?.pubUuid || '[noPubUuid]',
      infos: {
        urlEntry: this.urlEntryService.getParams(),
        uniqSession: this.eventMessagesService?.uniqSession || 'noUniqSession?',
        originalUserId: this.userService.user?.id || 'noUserId?',
        browser: `${this.eventMessagesService?.browserName} (${this.eventMessagesService?.browserVersion})`,
      },
      elapsedDetail: {
        elapsedInSeconds: this.calculateTimeElapsedInSeconds()
      },
      userExtProfilesAtAnswerSubmit: this.userService.user$.value.extProfiles
    }

  }

  private buildShrinkDataPayload() {

    const formattedAnswersOfActiveQuestions = {}

    this.activeSurvey$.value.pages.forEach(p => {
      p.questions.forEach(q => {
        if (q.keepInSurveyAnswers()) {
          formattedAnswersOfActiveQuestions[q.key] = q.formatFormPayload(this.activeSurvey$.value.form)
        }
      })
    })

    return {
      ...formattedAnswersOfActiveQuestions,
      version: '2022.0',
      timeStamp: new Date().toISOString(),
      pubUuid: this.publicationAccessService?.pubUuid || '[noPubUuid]',
      infos: {
        urlEntry: this.urlEntryService.getParams(),
        uniqSession: this.eventMessagesService?.uniqSession || 'noUniqSession?',
        originalUserId: this.userService.user?.id || 'noUserId?',
        browser: `${this.eventMessagesService?.browserName} (${this.eventMessagesService?.browserVersion})`,
      },
      elapsedDetail: {
        elapsedInSeconds: this.calculateTimeElapsedInSeconds()
      },
      userExtProfilesAtAnswerSubmit: this.userService.user$.value.extProfiles
    }

  }

  private calculateTimeElapsedInSeconds(): number {
    const startTime = new Date(this.activeSurvey$.value.form.get(this.activeSurvey$.value.startTimeControlName).value).getTime()
    const endTime = new Date(this.activeSurvey$.value.form.get(this.activeSurvey$.value.endTimeControlName).value).getTime()
    return ((endTime - startTime) / 1000) + (this.alreadyElapsedTime || 0);
  }

  private notifySuccessForPreviewMode() {
    this.toastr.info(
      `Les réponses au sondage sont valides, mais n'ont pas été enregistrées, car vous êtes en mode de prévisualisation. Le contenu qui serait sauvegardé est visible en console.`,
      'Sauvegarde des réponses',
      {timeOut: 10000}
    )
    console.info('%c Réponses au sondage ⬇️ ', 'color: #00A4FF', this.buildSurveySubmitBodyPayload())
  }

  private async syncAnswersWithFirebase(): Promise<void | boolean> {

    if (this.appModeService.appMode$.value === AppMode.SINGLE_QUESTION) {
      return false
    }

    const toSave = {
      timeStamp: new Date().toISOString(),
      datas: this.buildDataPayload(),
    }

    //
    // En mode test, on va aussi stocker les valeurs de haveRules au passage
    //
    if (this.appModeService.isTestSurvey$.value) {
      this.testSurveyService.updateValuesForTest(this.haveRules$.value, toSave);
    }
    //
    //
    //

    await this.db
      // this.db
      .object(`temp/${this.activeSurvey$.value.id}/${this.userService.user.id}`)
      .set(toSave)
      .then(() => {
        return of(true);
      })
      .catch((error) => {
        console.error('[syncAnswersWithFirebase] ERROR', error);
        return of(true);
      });

  }

  private applyMaxAnswers() {
    this.activeSurvey$.value.getQuestionsReferencedInStructureElements().forEach(q => {
      q.applyMaxAnswers(this.activeSurvey$.value.form)
    })
  }

  private initializeViewer(rawSurvey: ISurvey) {

    this.flowFeedbackService.setInitializeViewer(true);
    this.initialRawSurvey$.next(rawSurvey);
    this.haveRules$.next({...rawSurvey.haveRules});

    this.allFormControlsMarkedAsTouched$.next(false)

    if (this.formSub$) {
      this.formSub$.unsubscribe()
    }

    this.buildSurvey(rawSurvey)
    this.setupFormListener()
    this.flowFeedbackService.setInitializeViewer(false);

  }

  private setupFormListener() {
    this.updatedElapsedTime()
    this.formSub$ = this.activeSurvey$.value.form.valueChanges
      .subscribe(async () => {
        this.updatedElapsedTime()
        await this.syncAnswersWithFirebase()
      })
  }

  private updatedElapsedTime() {
    let now = new Date().toISOString()
    if (!this.activeSurvey$.value.form.get(this.activeSurvey$.value.startTimeControlName).value) {
      this.activeSurvey$.value.form.get(this.activeSurvey$.value.startTimeControlName).setValue(now, {emitEvent: false})
    }
    this.activeSurvey$.value.form.get(this.activeSurvey$.value.endTimeControlName).setValue(now, {emitEvent: false})
  }

  private buildSurvey(rawSurvey: ISurvey) {
    let survey


    //
    // Inject HonyPotQuestion
    //
    // En fait je vais toujours le mettre... on verra si il y a des réponses qui rentrent dans ça ;)
    //git
    rawSurvey = this.honeyPotService.injectHoneyPotQuestion(rawSurvey);

    const pub = this.publicationAccessService.publication$.value;


    try {
      if (this.appModeService.appMode$.value === AppMode.PREVIEW){
        this.langsListParamsService.updateLangsList(rawSurvey.surveyDef.activeLang || ['fr', 'en'])
      }

      survey = new Survey(
        rawSurvey,
        this.userService,
        {
          ...defaultSurveyConstructorParams,
          singlePreviewMode: this.appModeService.appMode$.value === AppMode.SINGLE_QUESTION,
          previewMode: this.appModeService.appMode$.value === AppMode.PREVIEW,
          applyRules:
            !(this.appModeService.appPreviewMode$.value === AppPreviewMode.TABLE || !this.appModeService.applyRules$.value),
          aliasTitle: pub.generalInfo.surveysAlias.doAliasSurvey ? pub.generalInfo.surveysAlias.aliases[rawSurvey.id] : undefined,
        },
      )
      this.activeSurvey$.next(survey)
      this.activeSurvey$.value.form.patchValue(this.answers$.value, {emitEvent: false})

    } catch (e) {
      console.error(e);
      Bugsnag.notify(e, (event) => {
        event.addMetadata('Survey Viewer Service', {
          survey: rawSurvey,
          errorMsg: e
        })
      })
      if (this.appModeService.appMode$.value !== AppMode.SINGLE_QUESTION) {
        this.toastr.error(e, this.translocoService.translate('Survey initialization failure'), {
          disableTimeOut: true
        })
      }
    }
  }

  isPreview(): boolean {
    return this.appModeService.appMode$.value === AppMode.PREVIEW;
  }

  private callValidateAnswerWithAI(submitBody: any, response: any): void {
    const endpoint = `${environment.apiUrl}/appCommunity/validateAnswerWithAI`;
    const body = {
      surveyId: submitBody.surveyId,
      userId: response.data.userDetails.userId,
    };

    console.log('[callValidateAnswerWithAI] Début validation AI:', {
      endpoint,
      body,
      surveyId: submitBody.surveyId,
      userId: response.data.userDetails.userId
    });

    console.log('[callValidateAnswerWithAI] Appel direct de la validation...');

    this.http.post(endpoint, body).subscribe({
      next: (validationResponse: any) => {
        console.log('[callValidateAnswerWithAI] Validation AI réussie:', validationResponse);
      },
      error: (err) => {
        console.error('[callValidateAnswerWithAI] Erreur validation AI:', err);
      }
    });
  }

}
